import { useEffect, useState } from 'react';
import { 
  FacebookIcon, 
  InstagramIcon, 
  LinkedInIcon, 
  XIcon 
} from '../../../components/SocialMediaIcons';
import PostNotes from './PostNotes';
import socialMediaApiService from '../../../services/socialMediaApiService';

export default function PostModal({ post, onClose, onEdit }) {
  const [isPosting, setIsPosting] = useState(false);
  const [postError, setPostError] = useState(null);
  const [postSuccess, setPostSuccess] = useState(false);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const generateSocialMediaUrl = (post) => {
    switch (post.platform) {
      case 'facebook':
        return `https://www.facebook.com/`;
      case 'instagram':
        return `https://www.instagram.com/`;
      case 'linkedin':
        return `https://www.linkedin.com/feed/`;
      case 'twitter':
        return `https://twitter.com/compose/tweet`;
      default:
        return '#';
    }
  };

  const handlePostNow = async () => {
    setIsPosting(true);
    setPostError(null);
    setPostSuccess(false);

    try {
      // Validate post content for platform limits
      const validation = socialMediaApiService.validatePostContent(post.platform, post.content);
      if (!validation.isValid) {
        throw new Error(`Post content exceeds ${post.platform} limit of ${validation.limit} characters. Current: ${validation.current}`);
      }

      // Format post data for the platform
      const postData = socialMediaApiService.formatPostForPlatform(post.platform, post);
      
      // Post to the platform
      const result = await socialMediaApiService.postToPlatform(post.platform, postData);
      
      setPostSuccess(true);
      
      // Update the post with the platform post ID for future metrics tracking
      if (result.postId || result.tweetId || result.mediaId) {
        const platformPostId = result.postId || result.tweetId || result.mediaId;
        // Here you would typically update the post in your database with the platform post ID
        console.log(`Posted to ${post.platform} with ID: ${platformPostId}`);
      }
      
      // Close modal after a short delay to show success message
      setTimeout(() => {
        onClose();
      }, 2000);
      
    } catch (error) {
      console.error('Error posting to social media:', error);
      
      // Provide more specific error messages
      let errorMessage = error.message;
      if (error.message.includes('No connection found')) {
        errorMessage = `Please connect your ${post.platform} account first in Account Settings → Social Media`;
      } else if (error.message.includes('not configured')) {
        errorMessage = `${post.platform} API is not configured. Please contact support.`;
      } else if (error.message.includes('exceeds')) {
        errorMessage = error.message; // Keep the character limit error as-is
      } else {
        errorMessage = `Failed to post to ${post.platform}. Please try again or use manual posting.`;
      }
      
      setPostError(errorMessage);
      
      // If API posting fails, fallback to opening the platform in a new tab
      if (error.message.includes('No connection found') || error.message.includes('not configured')) {
        setTimeout(() => {
          const url = generateSocialMediaUrl(post);
          window.open(url, '_blank');
        }, 2000); // Give user time to read the error message
      }
    } finally {
      setIsPosting(false);
    }
  };

  const getPlatformIcon = (iconId) => {
    switch (iconId) {
      case 'facebook':
        return FacebookIcon;
      case 'instagram':
        return InstagramIcon;
      case 'linkedin':
        return LinkedInIcon;
      case 'x':
        return XIcon;
      default:
        return FacebookIcon;
    }
  };

  if (!post) return null;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-3">
            <div className={`w-8 h-8 rounded-full ${post.platformColor} flex items-center justify-center text-white`}>
              {(() => {
                const PlatformIcon = getPlatformIcon(post.platformIcon);
                return <PlatformIcon size={20} />;
              })()}
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Post Details</h2>
              <p className="text-sm text-gray-500 capitalize">{post.platform}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl font-light"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Status and Date */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className={`px-3 py-1 rounded-full text-sm font-medium text-white ${post.statusColor}`}>
                {post.status}
              </span>
              {post.notes > 0 && (
                <span className="flex items-center space-x-1 text-sm text-gray-500">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span>{post.notes} notes</span>
                </span>
              )}
            </div>
            <div className="text-sm text-gray-500">
              {formatDate(post.date)}
            </div>
          </div>

          {/* Image */}
          {post.image && (
            <div className="rounded-lg overflow-hidden bg-gray-100">
              <img 
                src={post.image} 
                alt="Post" 
                className="w-full h-auto max-h-96 object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className="hidden w-full h-48 items-center justify-center text-gray-400">
                <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
          )}

          {/* Caption */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Post Content</h3>
            <p className="text-gray-900 whitespace-pre-wrap leading-relaxed">{post.caption}</p>
          </div>

          {/* Notes Section - Always Visible */}
          <div className="mt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Notes & Feedback</h3>
            <PostNotes postId={post.id} onClose={() => {}} />
          </div>

          {/* Error Message */}
          {postError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">{postError}</p>
              {postError.includes('connect your') && (
                <button
                  onClick={() => {
                    window.open('/dashboard/settings?tab=social', '_blank');
                  }}
                  className="mt-2 text-sm text-blue-600 hover:text-blue-800 underline"
                >
                  Go to Account Settings
                </button>
              )}
            </div>
          )}

          {/* Success Message */}
          {postSuccess && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-800">Post published successfully to {post.platform}!</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex space-x-3 pt-4 border-t">
            <button
              onClick={handlePostNow}
              disabled={isPosting}
              className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                postSuccess 
                  ? 'bg-green-600 text-white' 
                  : isPosting 
                    ? 'bg-gray-400 text-white cursor-not-allowed' 
                    : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {postSuccess ? 'Posted Successfully!' : isPosting ? 'Posting...' : 'Post Now'}
            </button>
            {onEdit && (
              <button
                onClick={() => {
                  onEdit(post);
                  onClose();
                }}
                className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
              >
                Edit Post
              </button>
            )}
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-500 hover:text-gray-700 text-sm font-medium transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}